/* eslint-disable max-len */
import { AppComponent } from './app.component';
import { HomePage } from './pages/home/home';
import { ErrorHandler, NgModule, Optional } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy } from '@angular/router';
import {
  IonicModule,
  IonicRouteStrategy,
  IonRouterOutlet,
} from '@ionic/angular';
import { AppRoutingModule } from './app-routing.module';
import {
  BUTTON_STYLE_CONFIGURATION,
  CommonUiModule,
  DATE_FORMAT_CONFIGURATION,
  IButtonStyleConfiguration,
  IDateFormatConfiguration,
  MultiTranslateHttpLoaderFactory,
} from '@bcl/common-ui';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from './material/material.module';
import {
  HttpBackend,
  HttpClientModule,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import {
  TranslateLoader,
  TranslateModule,
  TranslatePipe,
  TranslateService,
} from '@ngx-translate/core';
import { CommonModule, Location } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpRequestInterceptor } from './http-request-interceptor';
import { environment } from '../environments';
import {
  ApmModule,
  ApmService,
  ApmErrorHandler,
} from '@elastic/apm-rum-angular';
import { IonicStorageModule } from '@ionic/storage-angular';
import { RootComponent } from './root.component';
import {
  UsersManagementModule,
  CurrentUserService,
} from '@ews/users-management';
import { ConfigurationModule } from '@bcl/configuration';
import { AuthService } from './services/auth.service';

import { StorageService } from './services/storage.service';
import { SharedComponentsModule } from './shared-components/shared-components.module';
import { RestService } from './services/rest.service';
import { NetworkService } from './services/network.service';
import { VisitDateComponent } from './pages/home/components/visit-date/visit-date.component';
import { VisitTimeComponent } from './pages/home/components/visit-time/visit-time.component';
import { RestartDayDialogComponent } from './pages/home/components/restart-day-dialog/restart-day-dialog.component';
import { QuestionnaireSharedService } from './services/questionnaire-shared.service';
import { VisitDetailsPage } from './pages/visit-details/visit-details.page';
import { QuestionnairePage } from './pages/questionnaire/questionnaire.page';
import { QuestionPhotosPage } from './pages/question-photos/question-photos.page';
import { QuestionCategoryComponent } from './pages/questionnaire/components/question-category/question-category.component';
import { QuestionComponent } from './pages/questionnaire/components/question/question.component';
import { QuestionCommentsPage } from './pages/question-comments/question-comments.component';
import { ImageCropperModule } from 'ngx-image-cropper';
import { VisitDayPage } from './pages/visit-day/visit-day.page';
import { VisitDayElementComponent } from './pages/visit-day/components/visit-day-element/visit-day-element.component';
import { VisitDayDetailsPage } from './pages/visit-day-details/visit-day-details.page';
import { VisitDetailOverviewComponent } from './pages/visit-day-details/components/visit-detail-overview/visit-detail-overview.component';
import { VisitDetailInstructionComponent } from './pages/visit-day-details/components/visit-detail-instruction/visit-detail-instruction.component';
import { VisitDetailModulesComponent } from './pages/visit-day-details/components/visit-detail-modules/visit-detail-modules.component';
import { VisitDetailIpmComponent } from './pages/visit-day-details/components/visit-detail-ipm/visit-detail-ipm.component';
import { VisitLastVisitSummaryComponent } from './pages/visit-day-details/components/visit-last-visit-summary/visit-last-visit-summary.component';
import { Error403Interceptor } from './interceptors/interceptor403';
import { MainInterceptor } from './interceptors/interceptorMain';
import { DrawingControlModule } from '@bcl/drawing-control';
import { StateService } from './services/state.service';
import { UserService } from './services/user.service';
import { NotesListModule } from './shared-components/components/notes-list/notes-list.module';
import { NotesPage } from './pages/notes/notes.component';
import { ChatsPage } from './pages/chats/chats.component';
import {
  designSystemLocalization,
  DesignSystemModule,
} from '@bcl/design-system';
import { AreaPage } from './pages/area/area.component';
import { AreaMonitorPage } from './pages/area-monitor/area-monitor.component';
import { AreaMapPage } from './pages/area-map/area-map.component';
import { AreaDetailsIpmComponent } from './pages/area/components/area-details-ipm/area-details-ipm.component';
import { AreaDetailsMonitorsComponent } from './pages/area/components/area-details-monitors/area-details-monitors.component';
import { AreaDetailsInstructionsComponent } from './pages/area/components/area-details-instructions/area-details-instructions.component';
import { AreaMonitorActionsComponent } from './pages/area-monitor/components/area-monitor-actions/area-monitor-actions.component';
import { AreaMonitorOverviewComponent } from './pages/area-monitor/components/area-monitor-overview/area-monitor-overview.component';
import { ObservationDialogComponent } from './pages/area-monitor/components/observation-dialog/observation-dialog.component';
import { DetectionDialogComponent } from './pages/area/components/detection-dialog/detection-dialog.component';
import { TimesheetDialogComponent } from './pages/visit-day/components/timesheet-dialog/timesheet-dialog.component';
import { AreaMonitorStatusComponent } from './pages/area-monitor/components/area-monitor-status/area-monitor-status.component';
import { MonitorEditDialogComponent } from './pages/area-monitor/components/monitor-edit-dialog/monitor-edit-dialog.component';
import { AreaDetailsExtraMeasuresComponent } from './pages/area/components/area-details-extra-measures/area-details-extra-measures.component';
import { AreaDetailsDetectionsComponent } from './pages/area/components/area-details-detections/area-details-detections.component';
import { ObservationQuestionsDialogComponent } from './pages/area/components/area-details-monitors/observation-questions-dialog/observation-questions-dialog.component';
import { ActionPage } from './pages/action/action.component';
import { ActionCommentsComponent } from './pages/action/components/action-comments/action-comments.component';

import { MatProgressBarModule } from '@angular/material/progress-bar';
import { LightboxModule } from 'ngx-lightbox';
import { VisitDocumentsComponent } from './pages/visit-day-details/components/visit-documents/visit-documents.component';
import { AreaMonitorProductsComponent } from './pages/area-monitor/components/area-monitor-products/area-monitor-products.component';
import { ConsumablesDialogComponent } from './pages/area-monitor/components/consumables-dialog/consumables-dialog.component';
import { AddMonitorDialogComponent } from './pages/area-map/components/add-monitor-dialog/add-monitor-dialog.component';
import { VisitSummaryComponent } from './pages/visit-day-details/components/visit-summary/visit-summary.component';
import { VisitDetailReportToOfficeComponent } from './pages/visit-day-details/components/visit-detail-report-to-office/visit-detail-report-to-office.component';
import { ActionsListComponent } from './shared-components/components/actions-list/actions-list.component';

//Camera Scan
import { ZXingScannerModule } from '@zxing/ngx-scanner';
import { ActionDialogComponent } from './shared-components/components/actions-list/action-dialog/action-dialog.component';
import { AddMapActionDialogComponent } from './pages/area-map/components/add-map-action-dialog/add-map-action-dialog.component';
import { AreaDetailsSummaryComponent } from './pages/area/components/area-details-summary/area-details-summary.component';
import { NCComponent } from './pages/nc/nc.component';
import { NonconformitiesListModule } from './shared-components/components/nonconformities-list/nonconformities-list.module';
import { VisitDetailProductsComponent } from './pages/visit-day-details/components/visit-detail-products/visit-detail-products.component';
import { VisitConsumablesDialogComponent } from './pages/visit-day-details/components/visit-consumables-dialog/visit-consumables-dialog.component';
import { NonconformityPage } from './pages/nonconformity/nonconformity.component';
import { NonconformityDialogComponent } from './shared-components/components/nonconformities-list/nonconformity-dialog/nonconformity-dialog.component';
import { VisitDetailExpiredProductsComponent } from './pages/visit-day-details/components/visit-detail-expired-products/visit-detail-expired-products.component';
import { ExpiredProductsDialogComponent } from './pages/visit-day-details/components/expired-products-dialog/expired-products-dialog.component';
import { TotalCatchesDialogComponent } from './pages/visit-day-details/components/total-catches-dialog/total-catches-dialog.component';
import { CorrectDatesDialogComponent } from './pages/visit-day-details/components/correct-dates-dialog/correct-dates-dialog.component';
import { VisitDetailProductsUsageComponent } from './pages/visit-day-details/components/visit-detail-products-usage/visit-detail-products-usage.component';
import { ProductUsageDetailsDialogComponent } from './pages/visit-day-details/components/product-usage-details-dialog/product-usage-details-dialog.component';
import { CheckStockPageComponent } from './pages/check-stock-page/check-stock-page.component';
import { VisitStartModalComponent } from './pages/visit-day-details/components/visit-start-modal/visit-start-modal.component';
import { AreaMonitorCatchHistoryComponent } from './pages/area-monitor/components/area-monitor-catch-history/area-monitor-catch-history.component';
import { ReplaceMonitorDialogComponent } from './pages/area-monitor/components/replace-monitor-dialog/replace-monitor-dialog.component';
import { FollowUpAreasModulesDialogComponent } from './pages/visit-day-details/components/follow-up-areas-modules-dialog/follow-up-areas-modules-dialog.component';
import { ComponentsModule } from './components';
import { PipesModule } from './pipes/pipes.module';
import * as bclChat from '@bcl/chat';
import { ChatPage } from './pages/chat/chat.page';

@NgModule({
  declarations: [
    RootComponent,
    AppComponent,
    HomePage,
    VisitDateComponent,
    VisitTimeComponent,
    RestartDayDialogComponent,
    VisitDetailsPage,
    QuestionnairePage,
    QuestionPhotosPage,
    QuestionCommentsPage,
    QuestionCategoryComponent,
    QuestionComponent,
    VisitDayPage,
    VisitDayElementComponent,
    VisitDayDetailsPage,
    VisitDetailOverviewComponent,
    VisitDetailInstructionComponent,
    VisitDetailModulesComponent,
    VisitDetailIpmComponent,
    VisitLastVisitSummaryComponent,
    VisitDetailProductsComponent,
    NotesPage,
    ChatsPage,
    AreaPage,
    AreaMonitorPage,
    AreaMapPage,
    AreaDetailsIpmComponent,
    AreaDetailsMonitorsComponent,
    AreaDetailsInstructionsComponent,
    AreaMonitorActionsComponent,
    AreaMonitorOverviewComponent,
    ObservationDialogComponent,
    DetectionDialogComponent,
    TimesheetDialogComponent,
    AreaMonitorStatusComponent,
    MonitorEditDialogComponent,
    AreaDetailsExtraMeasuresComponent,
    AreaDetailsDetectionsComponent,
    ObservationQuestionsDialogComponent,
    ActionPage,
    ActionCommentsComponent,
    VisitDocumentsComponent,
    AreaMonitorProductsComponent,
    ConsumablesDialogComponent,
    AddMonitorDialogComponent,
    VisitSummaryComponent,
    VisitDetailReportToOfficeComponent,
    ActionsListComponent,
    ActionDialogComponent,
    AddMapActionDialogComponent,
    AreaDetailsSummaryComponent,
    NCComponent,
    VisitConsumablesDialogComponent,
    NonconformityPage,
    NonconformityDialogComponent,
    VisitDetailExpiredProductsComponent,
    ExpiredProductsDialogComponent,
    TotalCatchesDialogComponent,
    CorrectDatesDialogComponent,
    VisitDetailProductsUsageComponent,
    ProductUsageDetailsDialogComponent,
    CheckStockPageComponent,
    AreaMonitorCatchHistoryComponent,
    VisitStartModalComponent,
    ReplaceMonitorDialogComponent,
    FollowUpAreasModulesDialogComponent,
    ChatPage,
  ],
  imports: [
    ApmModule,
    BrowserModule,
    CommonModule,
    IonicModule.forRoot({ animated: false }),
    AppRoutingModule,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    HttpClientModule,
    CommonUiModule,
    MaterialModule,
    ImageCropperModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: MultiTranslateHttpLoaderFactory([
          designSystemLocalization(),
          { prefix: './assets/localization/', suffix: '.json' },
        ]),
        deps: [HttpBackend, Location],
      },
    }),
    UsersManagementModule.forRoot({
      authenticationUrl: environment.apiHost + '/authentication',
      configurationUrl: environment.apiHost + '/configuration',
    }),
    IonicStorageModule.forRoot(),
    FormsModule,
    ConfigurationModule.forRoot(environment),
    SharedComponentsModule,
    NotesListModule,
    DesignSystemModule,
    DrawingControlModule.forRoot(),
    ZXingScannerModule,
    MatProgressBarModule,
    LightboxModule,
    NonconformitiesListModule,
    ComponentsModule,
    PipesModule,
  ],
  bootstrap: [RootComponent],

  providers: [
    NetworkService,
    { provide: ErrorHandler, useClass: ApmErrorHandler },

    ApmService,
    QuestionnaireSharedService,
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpRequestInterceptor,
      multi: true,
    },
    {
      provide: DATE_FORMAT_CONFIGURATION,
      useValue: {
        baseFormat: 'short',
        formatOverrides: {
          short: 'dd.MM.yyyy HH:mm',
        },
      } as IDateFormatConfiguration,
    },
    {
      provide: BUTTON_STYLE_CONFIGURATION,
      useValue: {
        buttonStyles: { 'min-width': '150px' },
      } as IButtonStyleConfiguration,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MainInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: Error403Interceptor,
      multi: true,
    },
    {
      provide: bclChat.CHAT_CONFIGURATION,
      useValue: <bclChat.IChatConfiguration>{
        url: environment.apiHost + '/v1.0/chats',
        getNewMessagesIntervalInMilliseconds: 10 * 1000,
      },
    },

    TranslatePipe,
  ],
  exports: [],
})
export class AppModule {
  constructor(
    translateService: TranslateService,

    private readonly currentUserService: CurrentUserService,
    private readonly auth: AuthService,
    private readonly storage: StorageService,
    private readonly rest: RestService,
    private readonly stateService: StateService,
    private readonly userService: UserService,
    @Optional() private readonly routerOutlet?: IonRouterOutlet
  ) {
    translateService.setDefaultLang('en');

    this.currentUserService.user.subscribe((user) => {
      console.log('User changed => ', user);

      this.rest.updateUsername(user.userName);
      this.rest.updateEmail(user.email);
      this.rest.username = user.userName;
      this.rest.umId = user.id;

      if (!this.rest.username) {
        this.auth.logoutNew();
      }
      stateService.setValue('userId', user.id);

      stateService.setValue('userName', user.userName);
      stateService.setValue('userEmail', user.email);
      stateService.setValue(
        'isConnectedToActiveDirectory',
        user.isConnectedToActiveDirectory
      );
      userService.getUserInfo().subscribe((userInfo) => {
        if (userInfo?.officeId) {
          stateService.setValue('officeId', userInfo.officeId);
          this.rest.updateOfficeId(userInfo.officeId);
        }
        stateService.setValue('offices', userInfo.offices);
        this.rest.updateOffices(userInfo.offices);
        stateService.setValue('trackGpsLocation', userInfo.trackGpsLocation);
        stateService.setValue(
          'trackingFrequencyPlugged',
          userInfo.trackingFrequencyPlugged
        );
        stateService.setValue(
          'trackingFrequencyUnplugged',
          userInfo.trackingFrequencyUnplugged
        );
        stateService.setValue(
          'trackingHighPrecision',
          userInfo.trackingHighPrecision
        );
        stateService.setValue('defaultCar', userInfo.defaultCar);
        if (userInfo.language) {
          translateService.use(userInfo.language);
          this.storage.store('language', userInfo.language);
          stateService.setValue('language', userInfo.language);
          this.rest.updateLanguage(userInfo.language);
        } else {
          const office = userInfo.offices.find(
            (officeTemp) => officeTemp.id?.toString() === userInfo.officeId
          );
          translateService.use(office?.language ?? 'en');
          this.storage.store('language', office?.language);
          stateService.setValue('language', office?.language);
          this.rest.updateLanguage(office?.language);
        }
        const officeName = userInfo.offices.find(
          (office) => office.id.toString() === userInfo.officeId
        )?.name;
        if (officeName) {
          this.rest.updateOfficeName(officeName);
          this.rest.officeName = officeName;
        }

        this.stateService.isFirstTimeLoaded = true;
      });
    });
  }
}
